import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import persistReducer from "redux-persist/es/persistReducer";
import storage from "redux-persist/lib/storage";
import {
  _delete,
  _get,
  _getBlob,
  _patch,
  _post,
  _postFile,
} from "../../Services/baseApi";
import {
  CREATE_LOCATION_URL,
  LOCATION_DELETE_URL,
  LOCATION_DETAILS_URL,
  LOCATION_EXPORT_URL,
  LOCATION_IMPORT_URL,
  LOCATION_LIST_URL,
  LOCATION_SAMPLE_URL,
  LOCATION_UPDATE_URL,
} from "../../Services/endpoints";
import { errorHandler, successToast } from "../../Utils/toast";
import {
  downloadBlobFile,
  getFilenameFromDisposition,
} from "../../Utils/downloadFile";

const INITIAL_STATE = {
  locationList: [],
  locationDetails: {},
  isLoading: false,
  total: 0,
  page: 1,
  limit: 10,
};

const toOptionalNumber = (value) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};
const locationSlice = createSlice({
  name: "location",
  initialState: INITIAL_STATE,
  reducers: {
    setLocationDetails: (state, action) => {
      state.locationDetails = action.payload;
    },
  },
  extraReducers: (builder) => {
    // crate location
    builder
      .addCase(crateLocationThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(crateLocationThunk.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(crateLocationThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // get location list
    builder
      .addCase(getLocationListThunk.pending, (state, action) => {
        state.listRequestId = action.meta.requestId;
        state.isLoading = true;
      })
      .addCase(getLocationListThunk.fulfilled, (state, action) => {
        // Ignore a slower response for an older search/page.
        if (action.meta.requestId !== state.listRequestId) return;
        state.locationList = action?.payload?.findLocations || [];
        state.total = action?.payload?.totalCount || 0;
        state.page = action?.payload?.totalPageLength || 0;
        state.limit = action?.payload?.limit || 0;
        state.isLoading = false;
      })
      .addCase(getLocationListThunk.rejected, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        state.isLoading = false;
        state.error = action.payload;
      });

    // delete location
    builder
      .addCase(deleteLocationThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteLocationThunk.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteLocationThunk.rejected, (state) => {
        state.isLoading = false;
      });

    // update location
    builder
      .addCase(updateLocationThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateLocationThunk.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateLocationThunk.rejected, (state) => {
        state.isLoading = false;
      });

    // get location details
    builder
      .addCase(getLocationDetailsThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getLocationDetailsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.locationDetails = action.payload;
      })
      .addCase(getLocationDetailsThunk.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const { setLocationDetails } = locationSlice.actions;

export default persistReducer(
  {
    key: "location",
    storage,
  },
  locationSlice.reducer
);

// create location
export const crateLocationThunk = createAsyncThunk(
  "location/crateLocation",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _post(CREATE_LOCATION_URL, {
        name: payload?.location || undefined,
        stateId: payload?.state || undefined,
        cityId: payload?.city || undefined,
        zoneId: payload?.zone || undefined,
        wardId: payload?.ward || undefined,
        address: payload?.address || undefined,
        latitude: toOptionalNumber(payload?.latitude),
        longitude: toOptionalNumber(payload?.longitude),
      });
      if (response?.data) {
        successToast(response.data.message || "Location created successfully");
        payload?.handleFun?.(response.data?.result);

        return response.data;
      }
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// get location list
export const getLocationListThunk = createAsyncThunk(
  "location/getLocationList",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _get(LOCATION_LIST_URL, {
        page: payload?.page || undefined,
        limit: payload?.limit || undefined,
        name: payload?.searchText || undefined,
        key: payload?.sortColumn || "createdAt",
        keyOrder: payload?.sortDirection || "DESC",
        stateId: payload?.stateId || undefined,
        cityId: payload?.cityId || undefined,
        zoneId: payload?.zoneId || undefined,
        wardId: payload?.wardId || undefined,
        locationId: payload?.locationId || undefined,
        masterStatus: payload?.masterStatus || undefined,
        blinkerStatus: payload?.blinkerStatus || undefined,
      });

      if (response?.data) {
        return response.data.result;
      }
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// get location details
export const getLocationDetailsThunk = createAsyncThunk(
  "location/getLocationDetails",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _get(LOCATION_DETAILS_URL + `/${payload?.id}`);

      if (response?.data) {
        return response.data.result;
      }
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// update location
export const updateLocationThunk = createAsyncThunk(
  "location/updateLocation",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _patch(LOCATION_UPDATE_URL + `/${payload?.id}`, {
        id: payload?.id || undefined,
        name: payload?.location || undefined,
        stateId: payload?.state || undefined,
        cityId: payload?.city || undefined,
        zoneId: payload?.zone || undefined,
        wardId: payload?.ward || undefined,
        address: payload?.address || undefined,
        latitude: toOptionalNumber(payload?.latitude),
        longitude: toOptionalNumber(payload?.longitude),
      });
      if (response?.data) {
        successToast(response.data.message || "Location updated successfully");
        payload?.handleFun();
        return response.data;
      }
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// delete location
export const deleteLocationThunk = createAsyncThunk(
  "location/deleteLocation",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _delete(LOCATION_DELETE_URL + `/${payload?.id}`);
      if (response?.data) {
        successToast(response.data.message || "Location deleted successfully");
        payload?.handleFun();
        return response.data;
      }
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const exportLocationsThunk = createAsyncThunk(
  "location/export",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _getBlob(LOCATION_EXPORT_URL, payload?.params || {});
      const filename = getFilenameFromDisposition(
        response.headers["content-disposition"],
        "locations-export.xlsx",
      );
      downloadBlobFile(response.data, filename);
      successToast("Locations exported successfully");
      return true;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const downloadLocationSampleThunk = createAsyncThunk(
  "location/sample",
  async (_, { rejectWithValue }) => {
    try {
      const response = await _getBlob(LOCATION_SAMPLE_URL);
      const filename = getFilenameFromDisposition(
        response.headers["content-disposition"],
        "locations-sample.xlsx",
      );
      downloadBlobFile(response.data, filename);
      return true;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const importLocationsThunk = createAsyncThunk(
  "location/import",
  async ({ file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await _postFile(LOCATION_IMPORT_URL, formData);
      return response?.data;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);
